import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import logger from '@/lib/logger';
import { Brain, Barbell, Lightning, ArrowRight, Spinner, Check } from '@phosphor-icons/react';
import { ProGate } from '@/components/ProGate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOALS = ['fat_loss', 'muscle_tone', 'flexibility', 'stress_relief', 'endurance'];
const BODY_PARTS = ['full_body', 'abs', 'arms', 'chest', 'legs', 'back', 'shoulders'];

export default function AIPlanGenerator() {
  return (
    <ProGate feature="AI Workout Plan Generator">
      <AIPlanForm />
    </ProGate>
  );
}

function AIPlanForm() {
  const { user } = useAuth();
  const [goal, setGoal] = useState(user?.goals?.[0] || 'fat_loss');
  const [level, setLevel] = useState(user?.fitness_level || 'beginner');
  const [bodyFocus, setBodyFocus] = useState(user?.body_focus || ['full_body']);
  const [duration, setDuration] = useState(7);
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState('');

  const toggleBodyPart = (bp) => {
    setBodyFocus(prev => prev.includes(bp) ? prev.filter(b => b !== bp) : [...prev, bp]);
  };

  const generatePlan = async () => {
    setLoading(true);
    setError('');
    setPlan(null);
    try {
      const { data } = await axios.post(`${API}/workouts/generate-plan`, {
        goal,
        fitness_level: level,
        body_focus: bodyFocus.length ? bodyFocus : ['full_body'],
        duration_days: duration,
      });
      setPlan(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const planData = plan?.plan;

  return (
    <div data-testid="ai-plan-page">
      <div className="mb-8">
        <div className="label-tactical text-[#FF3B30] mb-1">AI-Powered</div>
        <h1 className="text-4xl font-black tracking-tighter">GENERATE YOUR PLAN</h1>
        <p className="text-[#A1A1AA] mt-2">Our AI creates a personalized workout plan based on your goals and fitness level.</p>
      </div>

      {!plan && (
        <div className="max-w-2xl">
          {/* Goal */}
          <div className="mb-6">
            <div className="label-tactical mb-3">Primary Goal</div>
            <div className="flex flex-wrap gap-2">
              {GOALS.map(g => (
                <button key={g} onClick={() => setGoal(g)} data-testid={`plan-goal-${g}`}
                  className={`px-4 py-2 text-sm font-bold uppercase tracking-wider transition-all ${goal === g ? 'bg-[#FF3B30] text-white' : 'tactical-card text-[#A1A1AA] hover:border-white/20'}`}>
                  {g.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>

          {/* Level */}
          <div className="mb-6">
            <div className="label-tactical mb-3">Fitness Level</div>
            <div className="flex flex-wrap gap-2">
              {['beginner', 'intermediate', 'advanced'].map(l => (
                <button key={l} onClick={() => setLevel(l)} data-testid={`plan-level-${l}`}
                  className={`px-4 py-2 text-sm font-bold uppercase tracking-wider transition-all ${level === l ? 'bg-[#FF3B30] text-white' : 'tactical-card text-[#A1A1AA] hover:border-white/20'}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          {/* Body Focus */}
          <div className="mb-6">
            <div className="label-tactical mb-3">Focus Areas</div>
            <div className="flex flex-wrap gap-2">
              {BODY_PARTS.map(bp => (
                <button key={bp} onClick={() => toggleBodyPart(bp)} data-testid={`plan-body-${bp}`}
                  className={`px-4 py-2 text-sm font-bold uppercase tracking-wider transition-all ${bodyFocus.includes(bp) ? 'bg-[#FF3B30] text-white' : 'tactical-card text-[#A1A1AA] hover:border-white/20'}`}>
                  {bp.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>

          {/* Duration */}
          <div className="mb-8">
            <div className="label-tactical mb-3">Plan Duration</div>
            <div className="flex gap-2">
              {[7, 14, 21, 28].map(d => (
                <button key={d} onClick={() => setDuration(d)} data-testid={`plan-duration-${d}`}
                  className={`px-5 py-3 text-lg font-bold transition-all ${duration === d ? 'bg-[#FF3B30] text-white' : 'tactical-card text-[#A1A1AA] hover:border-white/20'}`}>
                  {d} Days
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] px-4 py-3 mb-6 text-sm" data-testid="plan-error">
              {error}
            </div>
          )}

          <button onClick={generatePlan} disabled={loading} className="btn-primary px-8 py-4 text-lg flex items-center gap-2 disabled:opacity-50" data-testid="generate-plan-button">
            {loading ? (
              <><Spinner className="w-5 h-5 animate-spin" /> Generating with AI...</>
            ) : (
              <><Brain weight="bold" className="w-5 h-5" /> Generate Plan</>
            )}
          </button>
        </div>
      )}

      {/* Generated Plan Display */}
      {plan && planData && (
        <div data-testid="generated-plan">
          <div className="tactical-card p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold tracking-tight">{planData.plan_name || 'Your Custom Plan'}</h2>
                <p className="text-[#A1A1AA] text-sm mt-1">{planData.description}</p>
              </div>
              <button onClick={() => setPlan(null)} className="btn-secondary px-4 py-2 text-sm" data-testid="new-plan-button">
                New Plan
              </button>
            </div>
          </div>

          {/* Days */}
          <div className="space-y-4">
            {(planData.days || []).map((day) => (
              <div key={`day-${day.day || day.title}`} className="tactical-card p-5" data-testid={`plan-day-${day.day}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="label-tactical text-[#FF3B30]">Day {day.day}</span>
                    <h3 className="text-xl font-bold tracking-tight mt-1">{day.title || day.focus}</h3>
                  </div>
                  {day.estimated_minutes && (
                    <span className="text-sm text-[#71717A]">{day.estimated_minutes} min</span>
                  )}
                </div>
                {day.warmup && <p className="text-sm text-[#34C759] mb-2">Warmup: {day.warmup}</p>}
                <div className="space-y-2">
                  {(day.exercises || []).map((ex, j) => (
                    <div key={ex.name || `ex-${j}`} className="bg-[#1C1C1E] p-3 border border-white/5">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{ex.name}</span>
                        <span className="text-sm text-[#71717A]">{ex.sets} x {ex.reps}</span>
                      </div>
                      {ex.form_tip && <p className="text-xs text-[#34C759] mt-1">Tip: {ex.form_tip}</p>}
                      {ex.modification && <p className="text-xs text-[#FF9500] mt-1">Mod: {ex.modification}</p>}
                    </div>
                  ))}
                </div>
                {day.cooldown && <p className="text-sm text-[#007AFF] mt-2">Cooldown: {day.cooldown}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
